import { apiRequest } from "./client";

export type RegistrantType = "NATURAL_PERSON" | "LEGAL_ENTITY" | "OTHER_ORGANIZATION";
export type RegistrantWriteType = Exclude<RegistrantType, "OTHER_ORGANIZATION">;
export type RegistrantCategoryFilter = RegistrantType | "ORGANIZATION";
export type CertificateType = "IDENTITY_CARD" | "PASSPORT" | "UNIFIED_SOCIAL_CREDIT_CODE" | "OTHER";

export interface Registrant {
  id: string;
  public_id: string;
  owner_user_id: string;
  owner_name: string;
  registrant_type: RegistrantType;
  name: string;
  certificate_type: CertificateType;
  certificate_number_masked: string;
  version: number;
  has_active_certificate: boolean;
  has_pending_certificate: boolean;
  is_deleted: boolean;
  created_at: string;
  updated_at: string;
}

export interface RegistrantListResponse {
  items: Registrant[];
  total: number;
  page: number;
  page_size: number;
}

export interface RegistrantWritePayload {
  owner_user_id?: string;
  registrant_type: RegistrantWriteType;
  name: string;
  certificate_type: CertificateType;
  certificate_number?: string;
}

export function fetchRegistrants(
  filters: {
    page: number;
    pageSize: number;
    search?: string;
    ownerUserId?: string;
    includeDeleted?: boolean;
    registrantCategory?: RegistrantCategoryFilter;
  },
  signal?: AbortSignal,
): Promise<RegistrantListResponse> {
  const params = new URLSearchParams({
    page: String(filters.page),
    page_size: String(filters.pageSize),
  });
  if (filters.search) params.set("search", filters.search);
  if (filters.ownerUserId) params.set("owner_user_id", filters.ownerUserId);
  if (filters.includeDeleted) params.set("include_deleted", "true");
  if (filters.registrantCategory) params.set("registrant_type", filters.registrantCategory);
  return apiRequest(`/api/v1/registrants?${params}`, { signal });
}

export function createRegistrant(payload: RegistrantWritePayload): Promise<Registrant> {
  return apiRequest("/api/v1/registrants", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function updateRegistrant(
  id: string,
  payload: RegistrantWritePayload & { version: number },
): Promise<Registrant> {
  return apiRequest(`/api/v1/registrants/${id}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export function deleteRegistrant(id: string): Promise<void> {
  return apiRequest(`/api/v1/registrants/${id}`, { method: "DELETE" });
}

export function fetchCertificateNumber(
  id: string,
): Promise<{ certificate_type: CertificateType; certificate_number: string }> {
  return apiRequest(`/api/v1/registrants/${id}/certificate-number`);
}
